import React, { useState, useEffect, useRef } from "react";
import EngineeringIcon from "./EngineeringIcons";

interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  url: string;
  icon?: string;
  tags: string[];
}

const SearchModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Comprehensive search data
  const searchData: SearchResult[] = [
    // Main Pages
    {
      id: "home",
      title: "Home",
      description: "Ekumfi Engineering - Industrializing Ekumfiman through engineering excellence",
      category: "Pages",
      url: "/",
      icon: "🏠",
      tags: ["home", "main", "landing", "ekumfi", "engineering"]
    },
    {
      id: "about",
      title: "About Us",
      description: "Learn about Ekumfi Engineering's mission to industrialize Ekumfiman through 9 engineering keystones",
      category: "Pages",
      url: "/about",
      icon: "ℹ️",
      tags: ["about", "company", "mission", "vision", "history"]
    },
    {
      id: "team",
      title: "Our Team",
      description: "Meet the expert engineers and professionals behind Ekumfi Engineering",
      category: "Pages",
      url: "/team",
      icon: "👥",
      tags: ["team", "staff", "experts", "professionals", "engineers"]
    },
    {
      id: "projects",
      title: "Projects",
      description: "View our completed and ongoing engineering projects",
      category: "Pages",
      url: "/projects",
      icon: "📋",
      tags: ["projects", "portfolio", "work", "completed", "ongoing"]
    },
    {
      id: "contacts",
      title: "Contact Us",
      description: "Get in touch with Ekumfi Engineering for consultations and inquiries",
      category: "Pages",
      url: "/contacts",
      icon: "📞",
      tags: ["contact", "phone", "email", "location", "inquiry"]
    },

    // Engineering Keystones
    {
      id: "research-development",
      title: "Research & Development",
      description: "Pioneering breakthrough engineering solutions through cutting-edge research and development",
      category: "Engineering Keystones",
      url: "/services/research-development",
      icon: "🔬",
      tags: ["research", "development", "innovation", "technology", "R&D", "sustainable", "agricultural", "automation", "renewable"]
    },
    {
      id: "farm-machinery",
      title: "Farm Machinery & Food Processing",
      description: "Designing and fabricating advanced agricultural machinery and complete food processing lines",
      category: "Engineering Keystones",
      url: "/services/farm-machinery",
      icon: "🚜",
      tags: ["farm", "machinery", "agriculture", "food", "processing", "harvesting", "irrigation", "storage", "automation"]
    },
    {
      id: "energy-engineering",
      title: "Energy Engineering",
      description: "Revolutionary energy solutions including pyrolysis systems and electric-powered vehicles",
      category: "Engineering Keystones",
      url: "/services/energy-engineering",
      icon: "⚡",
      tags: ["energy", "pyrolysis", "electric", "vehicles", "solar", "wind", "renewable", "sustainable", "power"]
    },
    {
      id: "marine-engineering",
      title: "Boats & Yachts Building",
      description: "Expert marine engineering and construction of boats, yachts, and watercraft",
      category: "Engineering Keystones",
      url: "/services/marine-engineering",
      icon: "🚢",
      tags: ["marine", "boats", "yachts", "vessels", "fishing", "passenger", "cargo", "luxury", "watercraft"]
    },
    {
      id: "wood-engineering",
      title: "Wood Works Engineering",
      description: "Advanced wood processing and manufacturing solutions with precision cutting systems",
      category: "Engineering Keystones",
      url: "/services/wood-engineering",
      icon: "🪵",
      tags: ["wood", "timber", "processing", "cutting", "furniture", "manufacturing", "precision", "automation"]
    },
    {
      id: "plumbing-engineering",
      title: "Plumbing Engineering",
      description: "Comprehensive plumbing systems design and installation for all building types",
      category: "Engineering Keystones",
      url: "/services/plumbing-engineering",
      icon: "🔧",
      tags: ["plumbing", "water", "systems", "industrial", "commercial", "residential", "infrastructure", "pipes"]
    },
    {
      id: "sustainable-building",
      title: "Sustainable Building",
      description: "Innovative construction solutions including interlocking bricks and eco-friendly materials",
      category: "Engineering Keystones",
      url: "/services/sustainable-building",
      icon: "🏗️",
      tags: ["sustainable", "building", "construction", "interlocking", "bricks", "eco-friendly", "green", "materials"]
    },
    {
      id: "tvet-training",
      title: "TVET/Skills Training",
      description: "Comprehensive technical and vocational education training center for engineering skills",
      category: "Engineering Keystones",
      url: "/services/tvet-training",
      icon: "🎓",
      tags: ["training", "skills", "education", "technical", "vocational", "certification", "career", "development"]
    },
    {
      id: "software-ml",
      title: "Software & Machine Learning",
      description: "Cutting-edge software engineering and machine learning hub for digital transformation",
      category: "Engineering Keystones",
      url: "/services/software-ml",
      icon: "💻",
      tags: ["software", "machine learning", "AI", "automation", "digital", "transformation", "IoT", "intelligent"]
    },

    // Legacy Services
    {
      id: "welding",
      title: "Welding Services",
      description: "Professional welding and fabrication services for industrial and commercial applications",
      category: "Services",
      url: "/services/welding",
      icon: "🔥",
      tags: ["welding", "fabrication", "metal", "industrial", "commercial", "repair", "maintenance"]
    },
    {
      id: "fabrication",
      title: "Fabrication Services",
      description: "Custom metal fabrication and manufacturing services",
      category: "Services",
      url: "/services/fabrication",
      icon: "⚙️",
      tags: ["fabrication", "manufacturing", "custom", "metal", "production", "design"]
    },
    {
      id: "installation",
      title: "Installation Services",
      description: "Professional installation and commissioning services for engineering systems",
      category: "Services",
      url: "/services/installation",
      icon: "🔧",
      tags: ["installation", "commissioning", "setup", "equipment", "systems", "professional"]
    },
    {
      id: "maintenance",
      title: "Maintenance Services",
      description: "Comprehensive maintenance and repair services for engineering equipment and systems",
      category: "Services",
      url: "/services/maintenance",
      icon: "🛠️",
      tags: ["maintenance", "repair", "service", "equipment", "systems", "preventive", "corrective"]
    },

    // Training
    {
      id: "new-trainings",
      title: "New Trainings",
      description: "Latest training programs and courses offered by Ekumfi Engineering",
      category: "Training",
      url: "/training/new-trainings",
      icon: "📚",
      tags: ["training", "courses", "programs", "education", "skills", "learning", "certification"]
    },

    // Project Categories
    {
      id: "completed-projects",
      title: "Completed Projects",
      description: "View our portfolio of successfully completed engineering projects",
      category: "Projects",
      url: "/projects/completed",
      icon: "✅",
      tags: ["completed", "finished", "portfolio", "success", "achievements", "delivered"]
    },
    {
      id: "ongoing-projects",
      title: "Ongoing Projects",
      description: "Current engineering projects in progress at Ekumfi Engineering",
      category: "Projects",
      url: "/projects/ongoing",
      icon: "🔄",
      tags: ["ongoing", "current", "progress", "active", "development", "construction"]
    },

    // Individual Projects
    {
      id: "palm-threshing-machine-installation",
      title: "Palm Threshing Machine Installation",
      description: "High-efficiency palm fruit threshing machine designed for optimal fruit separation and processing",
      category: "Farm Machinery & Food Processing",
      url: "/projects/palm-threshing-machine-installation",
      icon: "🌾",
      tags: ["palm", "threshing", "machine", "installation", "farm", "processing", "automation", "completed"]
    },
    {
      id: "palm-kernel-oil-extraction",
      title: "Palm Kernel Oil Extraction System",
      description: "Advanced palm kernel oil extraction machine with high yield rate and automated processing",
      category: "Farm Machinery & Food Processing",
      url: "/projects/palm-kernel-oil-extraction",
      icon: "🛢️",
      tags: ["palm", "kernel", "oil", "extraction", "automation", "processing", "completed"]
    },
    {
      id: "industrial-drying-system",
      title: "Industrial Drying System",
      description: "Industrial-grade drying machine for agricultural products processing with energy-efficient operation",
      category: "Farm Machinery & Food Processing",
      url: "/projects/industrial-drying-system",
      icon: "🔥",
      tags: ["industrial", "drying", "system", "agricultural", "processing", "energy", "ongoing"]
    },
    {
      id: "pasteurizer-unit",
      title: "Pasteurizer Unit",
      description: "High-quality pasteurizer unit for food and beverage processing with advanced temperature control",
      category: "Farm Machinery & Food Processing",
      url: "/projects/pasteurizer-unit",
      icon: "🥛",
      tags: ["pasteurizer", "food", "beverage", "processing", "temperature", "control", "planned"]
    }
  ];

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
      setSearchQuery("");
      setResults([]);
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
      } else if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault();
        const result = results[selectedIndex];
        if (result) {
          window.location.href = result.url;
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const performSearch = (query: string) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    
    // Simulate search delay for better UX
    setTimeout(() => {
      const searchTerms = query.toLowerCase().split(" ");
      
      const filteredResults = searchData.filter(item => {
        const searchableText = [
          item.title.toLowerCase(),
          item.description.toLowerCase(),
          item.category.toLowerCase(),
          ...item.tags.map(tag => tag.toLowerCase())
        ].join(" ");

        return searchTerms.every(term => searchableText.includes(term));
      });

      // Sort by relevance (exact matches first, then partial matches)
      const sortedResults = filteredResults.sort((a, b) => {
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();
        const queryLower = query.toLowerCase();

        const aExactMatch = aTitle === queryLower;
        const bExactMatch = bTitle === queryLower;

        if (aExactMatch && !bExactMatch) return -1;
        if (!aExactMatch && bExactMatch) return 1;

        const aStartsWith = aTitle.startsWith(queryLower);
        const bStartsWith = bTitle.startsWith(queryLower);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return a.title.localeCompare(b.title);
      });

      setResults(sortedResults);
      setIsLoading(false);
    }, 150);
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleResultClick = (result: SearchResult) => {
    window.location.href = result.url;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-900 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
                <EngineeringIcon type="gear" size="sm" className="text-orange-500" animated={false} />
                Search Ekumfi Engineering
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <EngineeringIcon type="wrench" size="sm" className="text-gray-400" animated={false} />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Search for services, projects, or information..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="mt-4">
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                  <span className="ml-3 text-gray-600">Searching...</span>
                </div>
              )}

              {!isLoading && searchQuery && results.length === 0 && (
                <div className="text-center py-8">
                  <EngineeringIcon type="cog" size="lg" className="text-gray-300 mx-auto mb-4" animated={false} />
                  <p className="text-gray-500">No results found for "{searchQuery}"</p>
                  <p className="text-sm text-gray-400 mt-2">Try different keywords or browse our services</p>
                </div>
              )}

              {!isLoading && results.length > 0 && (
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {results.map((result, index) => (
                      <div
                        key={result.id}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          index === selectedIndex
                            ? "border-orange-500 bg-orange-50"
                            : "border-gray-200 hover:border-orange-300 hover:bg-gray-50"
                        }`}
                        onClick={() => handleResultClick(result)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="text-2xl">{result.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {result.title}
                              </h4>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {result.category}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {result.description}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {result.tags.slice(0, 3).map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isLoading && !searchQuery && (
                <div className="py-8">
                  <div className="text-center mb-6">
                    <EngineeringIcon type="gear" size="lg" className="text-orange-500 mx-auto mb-4 animate-spin-slow" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Quick Search</h4>
                    <p className="text-gray-600">Search for our engineering services, projects, or company information</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {["Research & Development", "Farm Machinery", "Energy Engineering", "Marine Engineering", "Wood Works", "Plumbing", "Sustainable Building", "Training"].map((service) => (
                      <button
                        key={service}
                        onClick={() => setSearchQuery(service)}
                        className="text-left p-3 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors"
                      >
                        <div className="text-sm font-medium text-gray-900">{service}</div>
                        <div className="text-xs text-gray-500">Engineering Service</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal; 